import PocketBase, { BaseAuthStore } from 'pocketbase';

/**
 * URL-Auflösung mit drei Stufen, damit ein vergessenes .env.production
 * den Login auf dem Server nicht abreißt:
 *
 *   1. VITE_POCKETBASE_URL  → wird zur Build-Zeit ins Bundle eingebettet.
 *      Setzen über frontend/.env oder frontend/.env.production.
 *   2. localhost-Erkennung  → lokale Entwicklung ohne env-Datei.
 *   3. Same-Origin /pb      → Production-Fallback, setzt voraus dass nginx
 *      /pb/* an die PocketBase-Instanz proxyt. Funktioniert auch dann,
 *      wenn das Build-Env vergessen wurde — es gibt also kein "Mobile sieht
 *      127.0.0.1" mehr.
 */
function resolveBackendUrl() {
  const fromEnv = import.meta.env.VITE_POCKETBASE_URL;
  if (fromEnv) return fromEnv;

  if (typeof window !== 'undefined' && window.location?.origin) {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://127.0.0.1:8090';
    }
    return `${window.location.origin}/pb`;
  }

  return 'http://127.0.0.1:8090';
}

const STORAGE_KEY = 'pocketbase_auth';

/**
 * Auth-Store, der in sessionStorage statt localStorage persistiert.
 * Effekt: Tab/Browser schließen → Token weg → User muss sich neu anmelden.
 * Bewusste DSGVO-Härtung für ein Vertriebs-CRM mit Patientendaten-Nähe.
 */
class SessionAuthStore extends BaseAuthStore {
  constructor() {
    super();
    if (typeof window === 'undefined') return;
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.token) {
        this.baseToken = parsed.token;
        this.baseModel = parsed.model ?? null;
      }
    } catch {
      // korrupter Eintrag → ignorieren, User landet auf Login
    }
  }

  save(token, model) {
    super.save(token, model);
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ token, model }));
    } catch {
      // sessionStorage voll / deaktiviert: Login bleibt in-memory aktiv
    }
  }

  clear() {
    super.clear();
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignorieren
    }
  }
}

// Migration: Token-Reste aus der LocalAuthStore-Ära wegräumen, damit kein
// stale Auth-Material zurückbleibt. Beim ersten Reload nach Deploy muss
// jeder User einmalig neu einloggen — gewollt.
if (typeof window !== 'undefined') {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignorieren
  }
}

/**
 * Singleton-PocketBase-Client für das Frontend.
 * Auth-Token lebt in sessionStorage — Tab schließen beendet die Session.
 */
export const pb = new PocketBase(resolveBackendUrl(), new SessionAuthStore());

// Verhindert, dass abgelaufene Tokens im Hintergrund hängen bleiben.
pb.autoCancellation(false);

const SESSION_ENDE_KEY = 'session_ende_grund';

/**
 * Hinterlegt, WARUM eine Session beendet wurde, damit die Login-Seite es
 * anzeigen kann. Läuft über sessionStorage statt über Router-State, weil der
 * AuthProvider außerhalb des BrowserRouter hängt (siehe App.jsx).
 */
export function merkeSessionEnde(grund) {
  try {
    window.sessionStorage.setItem(SESSION_ENDE_KEY, grund);
  } catch {
    // sessionStorage nicht verfügbar: dann eben ohne Begründung.
  }
}

/** Liest den Grund einmalig aus und räumt ihn weg. */
export function nimmSessionEnde() {
  try {
    const grund = window.sessionStorage.getItem(SESSION_ENDE_KEY);
    if (grund) window.sessionStorage.removeItem(SESSION_ENDE_KEY);
    return grund;
  } catch {
    return null;
  }
}

/**
 * Globaler Riegel für tote Sessions — greift z. B., wenn ein Admin den User
 * sperrt, während dessen Tab offen ist (der Sperr-Hook erneuert den tokenKey,
 * das vorhandene Token ist ab dann ungültig).
 *
 * Deckt allerdings nur den harten Fall ab: PocketBase ignoriert ein ungültiges
 * Token bei normalen Requests stillschweigend und behandelt sie als Gast —
 * Listen kommen dann als LEERE 200 zurück, nicht als Fehler. Ein 401 entsteht
 * nur auf Routen, die zwingend Auth verlangen (etwa auth-refresh). Den
 * leise-leeren Fall fängt die periodische Revalidierung in AuthContext.jsx ab.
 *
 * afterSend läuft vor dem Fehler-Throw des SDK und muss die Daten
 * durchreichen. Der isValid-Check verhindert, dass ein fehlgeschlagener
 * Login-Versuch hier fälschlich als "Session beendet" gewertet wird.
 */
pb.afterSend = (response, data) => {
  if (response.status === 401 && pb.authStore.isValid) {
    merkeSessionEnde(
      'Deine Sitzung ist nicht mehr gültig. Bitte melde dich erneut an.'
    );
    pb.authStore.clear();
  }
  return data;
};
